/**
 * ================================================================================================
 *  בשימוש לאחרונה — IN THE RAIL, UNDER ITS OWN HEADING. NOT A TAB STRIP.
 * ================================================================================================
 *
 * Owner instruction, 2026-10-25, verbatim:
 *
 *     *"יש טאבים לעמל - 'הארגון שלי' 'חיבורים ותמיה' 'החברות שלי' - זה לא צריך להיות בטאבים, זה יכול
 *     להיות מרוכז בצד שמאל תחת כותרת בשימוש לאחרונה כמו שיש בפוריטי"*
 *
 * ── WHAT THIS REPLACES, AND WHY THE TAB STRIP WAS WRONG HERE ────────────────────────────────────
 *
 * `WorkTabs` drew the customer kit's strip across the top of the content: a row of 190px tabs on a
 * `#e7eef9` band, the active one lifted with an inset blue rule. It was a faithful rendering of that
 * kit and it was the wrong component for this product, for a reason the Owner's screenshot shows in
 * one glance: the band sat directly above the page heading and its action, so `הקמת חברה` — a
 * primary button — came out looking like one more tab in the row above the quota panel. Two
 * different things with the same silhouette, nine pixels apart.
 *
 * A strip of tabs also claims something untrue. Tabs mean "these are views of the same thing, and
 * one of them is current". `הארגון שלי`, `חיבורים ותמיכה` and `החברות שלי` are three different
 * places. A list of recent places claims exactly what it is: where you have been, newest first.
 *
 * ── THE BEHAVIOUR IS THE SAME BEHAVIOUR, AND THAT IS DELIBERATE ─────────────────────────────────
 *
 * Everything below the rendering is carried over unchanged, because none of it was what was wrong:
 *
 *   · it follows the ADDRESS, not the click — a bookmark, a back button and a redirect are all ways
 *     to arrive, and a list that recorded only clicks would quietly disagree with where you are;
 *   · re-visiting a place MOVES it to the front rather than adding a second copy, so the list can
 *     never show one destination twice;
 *   · it is BOUNDED. An unbounded list becomes a second navigation that is worse than the first;
 *   · every storage read and write is wrapped. Storage throws in a private window and returns
 *     nothing with site data blocked, and a convenience list must never be why a screen fails to
 *     render. `glpoint.tabs` is kept as the key so nobody's list is emptied by this change.
 *
 * ── AND IT IS A LIST, NOT A SECOND SET OF NAVIGATION ────────────────────────────────────────────
 *
 * It renders UNDER the rail's own destinations, visually quieter than them, with its heading stated.
 * The rail stays authoritative: this is a shortcut back to somewhere you were, which is what the
 * Priority screen the Owner attached does with its own `בשימוש לאחרונה` block.
 *
 * It renders NOTHING when the list is empty — no heading over an empty box, which is the state a
 * person sees on their first visit and the one most likely to be mistaken for a fault.
 */

import { useCallback, useEffect, useState } from 'react';

import { useLocale } from '../i18n/LocaleProvider';
import type { Destination } from './destinations';

/** Small enough that the rail stays a rail, and that every entry is still readable. */
const MAX_RECENT = 5;
const KEY = 'glpoint.tabs';

function read(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // Private window, blocked site data, or corrupt JSON. An empty list is a working screen.
    return [];
  }
}

function write(value: readonly string[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // The list still applies to this session; it simply will not be remembered.
  }
}

/** The collapse choice, per device. Separate key: emptying the list must not re-expand the panel. */
const COLLAPSED_KEY = 'glpoint.recent.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    // Private window or blocked site data. Expanded is the state that shows the person their list.
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? 'true' : 'false');
  } catch {
    // It applies to this session and simply will not be remembered.
  }
}

export interface RecentlyUsedProps {
  readonly destinations: readonly Destination[];
  readonly currentPath: string;
}

export function RecentlyUsed({
  destinations,
  currentPath,
}: RecentlyUsedProps): JSX.Element | null {
  const { t } = useLocale();
  const [recent, setRecent] = useState<readonly string[]>(() => read());
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());

  useEffect(() => {
    const here =
      destinations.find((d) => d.href === currentPath) ??
      destinations.find((d) => (d.members ?? []).some((m) => m.href === currentPath));
    if (here === undefined) return;
    setRecent((previous) => {
      const next = [here.id, ...previous.filter((id) => id !== here.id)].slice(0, MAX_RECENT);
      write(next);
      return next;
    });
  }, [currentPath, destinations]);

  const forget = useCallback((id: string): void => {
    setRecent((previous) => {
      const next = previous.filter((x) => x !== id);
      write(next);
      return next;
    });
  }, []);

  const byId = new Map(destinations.map((d) => [d.id, d]));
  const open = recent.map((id) => byId.get(id)).filter((d): d is Destination => d !== undefined);
  if (open.length === 0) return null;

  return (
    <section
      className="gp-recent"
      aria-labelledby="gp-recent-title"
      {...(collapsed ? { 'data-collapsed': 'true' } : {})}
    >
      <div className="gp-recent__head">
        <h2 className="gp-recent__title" id="gp-recent-title">
          {t('nav.recentlyUsed')}
        </h2>
        {/*
          ── COLLAPSE, TO FREE THE SPACE. **Section 5, 2026-10-25.** ───────────────────────────

          *"אפשרות כיווץ לפינוי מקום."* A real `<button>`, so Tab reaches it and Space and Enter
          operate it with no key handler of our own. `aria-expanded` carries the state, and the
          label names the ACTION and changes with it — "collapse" while it is open is a label that
          describes the wrong situation.

          The choice is remembered per device, through the same wrapped storage the list uses, and
          a failure to remember it is not a failure to render.
        */}
        <button
          type="button"
          className="gp-recent__toggle"
          aria-expanded={!collapsed}
          aria-controls="gp-recent-list"
          aria-label={collapsed ? t('nav.recentExpand') : t('nav.recentCollapse')}
          onClick={() => {
            setCollapsed((previous) => {
              writeCollapsed(!previous);
              return !previous;
            });
          }}
        >
          <span aria-hidden="true">{collapsed ? '«' : '»'}</span>
        </button>
      </div>
      <ul className="gp-recent__list" id="gp-recent-list">
        {open.map((d) => {
          const href = d.href ?? (d.members ?? [])[0]?.href;
          const here = href === currentPath;
          return (
            <li className="gp-recent__item" key={d.id}>
              {/*
                `aria-current="page"` and NOT a colour alone. The current entry is marked for a
                screen reader by the attribute, for everyone by the heavier weight, and by the rule
                at the inline start — three signals, none of which is hue. (§3, 2026-10-25.)
              */}
              <a
                className="gp-recent__link"
                href={href ?? '#'}
                {...(here ? { 'aria-current': 'page' as const } : {})}
              >
                {t(d.label)}
              </a>
              <button
                type="button"
                className="gp-recent__forget"
                aria-label={`${t(d.label)} — ${t('nav.removeFromRecent')}`}
                onClick={() => forget(d.id)}
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
